/**
 * Utilitaire de logging avec styles colorés
 */

// Types de logs disponibles
const LOG_TYPES = {
  INFO: {
    background: '#2196f3',
    color: 'white',
    emoji: 'ℹ️'
  },
  SUCCESS: {
    background: '#4caf50',
    color: 'white',
    emoji: '✅'
  },
  WARNING: {
    background: '#ff9800',
    color: 'black',
    emoji: '⚠️'
  },
  ERROR: {
    background: '#f44336',
    color: 'white',
    emoji: '❌'
  },
  DEBUG: {
    background: '#9c27b0',
    color: 'white',
    emoji: '🔍'
  }
};

/**
 * Crée un style CSS pour les logs
 * @param {Object} options - Options de style
 * @returns {string} - Style CSS
 */
const createStyle = (options = {}) => {
  const {
    background = '#2196f3',
    color = 'white',
    fontSize = '12px',
    fontWeight = 'bold',
    padding = '2px 5px',
    borderRadius = '2px'
  } = options;

  return `background: ${background}; color: ${color}; font-size: ${fontSize}; font-weight: ${fontWeight}; padding: ${padding}; border-radius: ${borderRadius};`;
};

/**
 * Logger principal
 */
const Logger = {
  /**
   * Log avec style personnalisé
   * @param {string} message - Message à logger
   * @param {Object} data - Données additionnelles à logger
   * @param {Object} styleOptions - Options de style
   */
  log: (message, data, styleOptions = {}) => {
    const style = createStyle(styleOptions);
    if (data !== undefined) {
      console.log(`%c${message}`, style, data);
    } else {
      console.log(`%c${message}`, style);
    }
  },

  /**
   * Log d'information
   * @param {string} message - Message à logger
   * @param {Object} data - Données additionnelles à logger
   */
  info: (message, data) => {
    const { background, color, emoji } = LOG_TYPES.INFO;
    const formattedMessage = `${emoji} INFO: ${message}`;
    Logger.log(formattedMessage, data, { background, color });
  },

  /**
   * Log de succès
   * @param {string} message - Message à logger
   * @param {Object} data - Données additionnelles à logger
   */
  success: (message, data) => {
    const { background, color, emoji } = LOG_TYPES.SUCCESS;
    const formattedMessage = `${emoji} SUCCÈS: ${message}`;
    Logger.log(formattedMessage, data, { background, color });
  },

  /**
   * Log d'avertissement
   * @param {string} message - Message à logger
   * @param {Object} data - Données additionnelles à logger
   */
  warning: (message, data) => {
    const { background, color, emoji } = LOG_TYPES.WARNING;
    const formattedMessage = `${emoji} ATTENTION: ${message}`;
    Logger.log(formattedMessage, data, { background, color });
  },

  /**
   * Log d'erreur
   * @param {string} message - Message à logger
   * @param {Object} data - Données additionnelles à logger
   */
  error: (message, data) => {
    const { background, color, emoji } = LOG_TYPES.ERROR;
    const formattedMessage = `${emoji} ERREUR: ${message}`;
    Logger.log(formattedMessage, data, { background, color });
  },

  /**
   * Log de débogage
   * @param {string} message - Message à logger
   * @param {Object} data - Données additionnelles à logger
   */
  debug: (message, data) => {
    const { background, color, emoji } = LOG_TYPES.DEBUG;
    const formattedMessage = `${emoji} DEBUG: ${message}`;
    Logger.log(formattedMessage, data, { background, color });
  },

  /**
   * Log important avec style très visible
   * @param {string} message - Message à logger
   * @param {Object} data - Données additionnelles à logger
   */
  important: (message, data) => {
    const style = {
      background: '#e91e63',
      color: 'white',
      fontSize: '16px',
      padding: '4px 8px',
      borderRadius: '4px'
    };
    const formattedMessage = `⚡⚡⚡ ${message} ⚡⚡⚡`;
    Logger.log(formattedMessage, data, style);
  },

  /**
   * Crée un logger spécifique à un module
   * @param {string} moduleName - Nom du module
   * @param {Object} styleOptions - Options de style
   * @returns {Object} - Logger spécifique au module
   */
  createModuleLogger: (moduleName, styleOptions = {}) => {
    return {
      log: (message, data) => Logger.log(`[${moduleName}] ${message}`, data, styleOptions),
      info: (message, data) => Logger.info(`[${moduleName}] ${message}`, data),
      success: (message, data) => Logger.success(`[${moduleName}] ${message}`, data),
      warning: (message, data) => Logger.warning(`[${moduleName}] ${message}`, data),
      error: (message, data) => Logger.error(`[${moduleName}] ${message}`, data),
      debug: (message, data) => Logger.debug(`[${moduleName}] ${message}`, data),
      important: (message, data) => Logger.important(`[${moduleName}] ${message}`, data)
    };
  }
};

export default Logger;
